/**
 * Hybrid builder preview: match product metafield metaobject overrides to `completeOptions`,
 * then drive `media-gallery` (product media slide) or an overlay (`external_preview_url` when the
 * file is not on the product).
 */
(function () {
  "use strict";

  function alpineData(el) {
    if (!el) return null;
    if (window.Alpine && typeof Alpine.$data === "function") {
      try {
        return Alpine.$data(el);
      } catch (e) {}
    }
    if (el._x_dataStack && el._x_dataStack[0]) return el._x_dataStack[0];
    return null;
  }

  function norm(s) {
    return String(s ?? "")
      .trim()
      .toLowerCase();
  }

  function readOverridesConfig(sectionId) {
    const el = document.getElementById("BuilderPreviewOverrides-" + sectionId);
    if (!el || !el.textContent) return null;
    try {
      const rows = JSON.parse(el.textContent);
      if (!Array.isArray(rows) || rows.length === 0) return null;
      return { sectionId, rows };
    } catch (e) {
      return null;
    }
  }

  /**
   * Must use the same active form as hub/wheel tabs (selectedTab + panel index). Relying on
   * getComputedStyle(display) breaks when tabpanels are hidden (mobile drawer), x-show mid-transition,
   * or when the first matching hubBuilderForm in the DOM is the wrong tab (e.g. "complete" vs "rear").
   */
  function getActiveBuilderFormRoot() {
    const root = document.querySelector(".wheelbuilder");
    if (!root) return null;
    const tryTabs = (sel) => {
      const tabsEl = root.querySelector(sel);
      if (!tabsEl || !window.Alpine || typeof Alpine.$data !== "function") return null;
      try {
        const tabsData = Alpine.$data(tabsEl);
        if (tabsData && typeof tabsData.getActiveFormElement === "function") {
          const form = tabsData.getActiveFormElement();
          if (form) return form;
        }
      } catch (e) {}
      return null;
    };
    const fromHub = tryTabs('[x-data^="hubBuilderTabs"]');
    if (fromHub) return fromHub;
    const fromWheel = tryTabs('[x-data^="wheelBuilderTabs"]');
    if (fromWheel) return fromWheel;

    const panels = root.querySelectorAll("section[role='tabpanel']");
    for (let i = 0; i < panels.length; i++) {
      const panel = panels[i];
      if (window.getComputedStyle(panel).display === "none") continue;
      const hub = panel.querySelector('[x-data^="hubBuilderForm"]');
      if (hub) return hub;
      const wheel = panel.querySelector('[x-data^="wheelBuilderForm"]');
      if (wheel) return wheel;
    }
    return (
      root.querySelector('[x-data^="hubBuilderForm"]') ||
      root.querySelector('[x-data^="wheelBuilderForm"]')
    );
  }

  function selectionFromForm(formEl) {
    const data = alpineData(formEl);
    const opts = data && data.completeOptions;
    if (!Array.isArray(opts)) return null;
    const out = {};
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      if (!o || !o.id) continue;
      const v = o.value;
      if (v == null || String(v).trim() === "") continue;
      out[String(o.id)] = String(v).trim();
    }
    return out;
  }

  /** Hub/wheel options sometimes use hub_colour vs hub_color (same for spoke). */
  function selectionForKey(selection, optionKey) {
    if (!selection || optionKey == null) return null;
    const k = String(optionKey);
    const direct = selection[k];
    if (direct != null && String(direct).trim() !== "") {
      return String(direct).trim();
    }
    const pairs = [
      ["hub_color", "hub_colour"],
      ["hub_colour", "hub_color"],
      ["spoke_color", "spoke_colour"],
      ["spoke_colour", "spoke_color"],
    ];
    for (let p = 0; p < pairs.length; p++) {
      if (pairs[p][0] === k) {
        const alt = selection[pairs[p][1]];
        if (alt != null && String(alt).trim() !== "") return String(alt).trim();
      }
    }
    return null;
  }

  function pickWinner(rows, selection) {
    let best = null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const key = row.option_key;
      if (!key) continue;
      const sel = selectionForKey(selection, key);
      if (sel == null || sel === "") continue;
      if (norm(sel) !== norm(row.option_value)) continue;
      const pr = Number(row.priority) || 0;
      const mid = row.media_id;
      const ext =
        row.external_preview_url &&
        String(row.external_preview_url).trim() !== ""
          ? String(row.external_preview_url).trim()
          : null;
      const hasMid = mid != null && mid !== "" && !Number.isNaN(Number(mid));
      if (!hasMid && !ext) continue;
      if (
        !best ||
        pr > best.pr ||
        (pr === best.pr && i < best.idx)
      ) {
        best = {
          pr,
          idx: i,
          mediaId: hasMid ? Number(mid) : null,
          externalPreviewUrl: ext,
        };
      }
    }
    return best;
  }

  function dispatchPreview(sectionId, detail) {
    document.dispatchEvent(
      new CustomEvent("wheelbuilder:preview-override", {
        bubbles: true,
        detail: Object.assign({ sectionId: String(sectionId) }, detail),
      })
    );
  }

  function syncGalleryForSection(sectionId) {
    const cfg = readOverridesConfig(sectionId);
    if (!cfg) return;
    const form = getActiveBuilderFormRoot();
    const selection = selectionFromForm(form);
    if (!selection) {
      dispatchPreview(sectionId, { reset: true });
      return;
    }
    const winner = pickWinner(cfg.rows, selection);
    if (!winner) {
      dispatchPreview(sectionId, { reset: true });
      return;
    }
    if (winner.externalPreviewUrl) {
      dispatchPreview(sectionId, {
        externalPreviewUrl: winner.externalPreviewUrl,
      });
    } else {
      dispatchPreview(sectionId, { mediaId: winner.mediaId });
    }
  }

  function findSectionIds() {
    const els = document.querySelectorAll(
      'script[type="application/json"][id^="BuilderPreviewOverrides-"]'
    );
    const ids = [];
    els.forEach((el) => {
      const id = el.getAttribute("data-section-id") || el.id.replace("BuilderPreviewOverrides-", "");
      if (id) ids.push(id);
    });
    return ids;
  }

  function runAll() {
    const ids = findSectionIds();
    for (let i = 0; i < ids.length; i++) syncGalleryForSection(ids[i]);
  }

  /** Preset deep links: `?wb_media=1` (1-based) or `cfg=...;img=2` — applied once after hybrid preview sync. */
  var wbUrlInitialMediaDone = false;

  function readLandingMediaIndex1Based() {
    try {
      var u = new URL(window.location.href);
      var qp = u.searchParams.get("wb_media");
      if (qp != null && String(qp).trim() !== "") {
        var n1 = parseInt(String(qp).trim(), 10);
        if (Number.isFinite(n1) && n1 >= 1) return n1;
      }
      var rawCfg = u.searchParams.get("cfg");
      if (!rawCfg) return null;
      var parts = String(rawCfg).split(";");
      for (var pi = 0; pi < parts.length; pi++) {
        var p = parts[pi];
        var eq = p.indexOf("=");
        if (eq < 0) continue;
        var key = p.slice(0, eq).trim();
        var val = p.slice(eq + 1).trim();
        try {
          key = decodeURIComponent(key).toLowerCase();
          val = decodeURIComponent(val);
        } catch (e) {}
        if (key === "img" || key === "media" || key === "i") {
          var n2 = parseInt(String(val).trim(), 10);
          if (Number.isFinite(n2) && n2 >= 1) return n2;
        }
      }
    } catch (e2) {}
    return null;
  }

  function applyUrlInitialWbMedia(e) {
    if (wbUrlInitialMediaDone) return;
    var idx1 = readLandingMediaIndex1Based();
    if (idx1 == null) return;
    var root =
      e && e.target && e.target.closest
        ? e.target.closest("media-gallery")
        : null;
    if (!root || !root.closest(".wheelbuilder")) return;
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId) return;
    var slider = root.querySelector('[id^="Media-Gallery-"]');
    if (!slider) return;
    var slides = slider.querySelectorAll(".f-product__media");
    if (!slides || slides.length === 0) return;
    var zero = Math.min(slides.length, Math.max(1, idx1)) - 1;
    var idAttr = slides[zero] && slides[zero].getAttribute("data-media-id");
    if (idAttr == null || idAttr === "") return;
    var mediaId = Number(idAttr);
    if (Number.isNaN(mediaId)) return;
    dispatchPreview(sectionId, { mediaId: mediaId });
    wbUrlInitialMediaDone = true;
  }

  document.addEventListener("wheelbuilder:form-state-changed", function () {
    runAll();
  });
  document.addEventListener("wheelbuilder:tab-changed", function () {
    runAll();
  });
  document.addEventListener("wheelbuilder:selections-cleared", function () {
    runAll();
  });
  document.addEventListener("product-media:loaded", function (e) {
    runAll();
    applyUrlInitialWbMedia(e);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runAll);
  } else {
    setTimeout(runAll, 0);
  }

  /**
   * Carousel prev/next should only change the active slide — not bubble to outer `.wheelbuilder`
   * handlers (drawer, tabs, etc.).
   */
  function isolateBuilderGalleryNav() {
    var roots = document.querySelectorAll(".wheelbuilder");
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r];
      if (root.getAttribute("data-wb-gallery-nav-isolate") === "1") continue;
      root.setAttribute("data-wb-gallery-nav-isolate", "1");
      root.addEventListener(
        "click",
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          if (t.closest(".wheelbuilder-product-gallery .flickity-button")) {
            e.stopPropagation();
          }
        },
        false
      );
    }
  }

  document.addEventListener("product-media:loaded", isolateBuilderGalleryNav);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", isolateBuilderGalleryNav);
  } else {
    setTimeout(isolateBuilderGalleryNav, 0);
  }
})();
